import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconArchive, IconArrowBackUp, IconCheck, IconPencil, IconPin, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  REPLY_RULE_KINDS,
  createReplyRule,
  listReplyRules,
  patchReplyRule,
  putReplyRuleSettings,
  type ReplyRule,
  type ReplyRuleCounts,
  type ReplyRuleKind,
  type ReplyRuleStatus,
} from '@mygpt/app/apis/services/replyRules';
import { CARD_BORDER, errorDetail, formatJst } from '@/components/Datasets/KnowledgeLoop/shared';

type View = ReplyRuleStatus;

/** bot 級「回答ルール」：ドラフト生成の prompt にだけ注入される書き方・方針。スイッチ OFF の bot は何もしない */
export const ReplyRulesPanel = ({ robotId }: { robotId: string }) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [view, setView] = useState<View>('active');
  const [items, setItems] = useState<ReplyRule[]>([]);
  const [counts, setCounts] = useState<ReplyRuleCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<ReplyRule | 'new' | null>(null);
  const [formKind, setFormKind] = useState<ReplyRuleKind>('style_rule');
  const [formText, setFormText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listReplyRules(robotId, view);
      setEnabled(data.enabled);
      setItems(data.items || []);
      setCounts(data.counts || null);
    } catch (e) {
      console.error('load reply rules failed:', e);
      showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
    } finally {
      setLoading(false);
    }
  }, [robotId, view, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (on: boolean) => {
    setBusy('switch');
    try {
      const { data } = await putReplyRuleSettings(robotId, on);
      setEnabled(data.enabled);
      showNotification({
        color: 'green',
        message: t(data.enabled ? 'zendesk_ops.rules.enabled_on' : 'zendesk_ops.rules.enabled_off'),
      });
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  const groups = useMemo(
    () => REPLY_RULE_KINDS.map((kind) => ({ kind, rows: items.filter((r) => r.kind === kind) })),
    [items]
  );

  const openEditor = (target: ReplyRule | 'new') => {
    setEditing(target);
    setFormKind(target === 'new' ? 'style_rule' : target.kind);
    setFormText(target === 'new' ? '' : target.text);
  };

  const submit = async () => {
    if (!editing || formText.trim().length < 4) return;
    setBusy('form');
    try {
      if (editing === 'new') await createReplyRule(robotId, { kind: formKind, text: formText.trim() });
      else await patchReplyRule(robotId, editing.id, { kind: formKind, text: formText.trim() });
      setEditing(null);
      showNotification({ color: 'green', message: t('zendesk_ops.rules.saved') });
      load();
    } catch (e) {
      // 数値・日付を含むルールは後端が 400（事実はナレッジへ）
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (r: ReplyRule, status: 'active' | 'archived') => {
    setBusy(r.id);
    try {
      await patchReplyRule(robotId, r.id, { status });
      load();
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  const asIsRate = (r: ReplyRule) => (r.hits ? Math.round((r.as_is_hits / r.hits) * 100) : null);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div>
          <Group gap={10} wrap="nowrap">
            <Switch
              size="sm"
              checked={!!enabled}
              disabled={enabled === null || busy === 'switch'}
              onChange={(e) => toggle(e.currentTarget.checked)}
              label={t('zendesk_ops.rules.switch')}
            />
          </Group>
          <Text size="xs" c="gray.6" mt={4} maw={720}>
            {t('zendesk_ops.rules.switch_desc')}
          </Text>
        </div>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => openEditor('new')}>
          {t('zendesk_ops.rules.add')}
        </Button>
      </Group>

      {enabled === false && (
        <Alert color="gray" variant="light">
          <Text size="xs">{t('zendesk_ops.rules.off_hint')}</Text>
        </Alert>
      )}

      <Group justify="space-between">
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as View)}
          data={(['active', 'proposed', 'archived'] as View[]).map((v) => ({
            value: v,
            label: `${t(`zendesk_ops.rules.view_${v}`)}${counts?.status?.[v] ? ` (${counts.status[v]})` : ''}`,
          }))}
        />
        <Group gap={6}>
          {REPLY_RULE_KINDS.map((kind) => {
            const n = groups.find((g) => g.kind === kind)?.rows.length || 0;
            return (
              <Tooltip key={kind} label={t(`zendesk_ops.rules.kind_desc.${kind}`)} withArrow multiline w={280}>
                <Badge
                  variant={n ? 'light' : 'outline'}
                  color={n ? 'violet' : 'gray'}
                  size="sm"
                  style={{ textTransform: 'none' }}
                >
                  {t(`zendesk_ops.rules.kind.${kind}`)} {n}
                </Badge>
              </Tooltip>
            );
          })}
        </Group>
      </Group>
      <Text size="xs" c="gray.6">
        {t(`zendesk_ops.rules.view_${view}_desc`)}
      </Text>

      {loading ? (
        <Group justify="center" py={60}>
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Stack align="center" gap={4} py={50}>
          <Text fw={500}>{t('zendesk_ops.rules.empty')}</Text>
          <Text size="xs" c="gray.6" ta="center" maw={520}>
            {t('zendesk_ops.rules.empty_desc')}
          </Text>
        </Stack>
      ) : (
        groups
          .filter((g) => g.rows.length)
          .map(({ kind, rows }) => (
            <div key={kind} style={{ border: CARD_BORDER, borderRadius: 12, background: '#fff' }}>
              <Group justify="space-between" px="md" py={10} style={{ borderBottom: CARD_BORDER }}>
                <Group gap={8}>
                  <Text fw={600} size="sm">
                    {t(`zendesk_ops.rules.kind.${kind}`)}
                  </Text>
                  <Badge size="sm" variant="light" color="violet">
                    {rows.length}
                  </Badge>
                </Group>
                <Text size="xs" c="gray.6">
                  {t(`zendesk_ops.rules.kind_desc.${kind}`)}
                </Text>
              </Group>
              <Stack gap={0}>
                {rows.map((r, i) => {
                  const rate = asIsRate(r);
                  const why = r.evidence?.find((e) => e.why)?.why;
                  return (
                    <Group
                      key={r.id}
                      wrap="nowrap"
                      align="flex-start"
                      gap="md"
                      px="md"
                      py={10}
                      style={{ borderTop: i ? CARD_BORDER : undefined }}
                    >
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.text}
                        </Text>
                        {why && view === 'proposed' && (
                          <Text size="xs" c="gray.6">
                            {t('zendesk_ops.rules.why')}：{why}
                          </Text>
                        )}
                        <Group gap={8}>
                          <Badge size="xs" variant="outline" color={r.origin === 'auto' ? 'gray' : 'violet'}>
                            {t(`knowledge_loop.memory.origin.${r.origin}`)}
                          </Badge>
                          {r.pinned && (
                            <Tooltip label={t('knowledge_loop.memory.pinned_desc')} withArrow>
                              <Group gap={2}>
                                <IconPin size={12} color="#7048E8" />
                                <Text size="xs" c="violet">
                                  {t('knowledge_loop.memory.pinned')}
                                </Text>
                              </Group>
                            </Tooltip>
                          )}
                          <Text size="xs" c="gray.6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {t('zendesk_ops.rules.usage', { hits: r.hits, as_is: r.as_is_hits })}
                            {rate !== null ? `（${rate}%）` : ''}
                            {r.last_used_at ? ` ・ ${formatJst(r.last_used_at)}` : ''}
                            {r.created_by ? ` ・ ${r.created_by}` : ''}
                          </Text>
                        </Group>
                      </Stack>
                      <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                        {view === 'proposed' && (
                          <Tooltip label={t('zendesk_ops.rules.confirm')} withArrow>
                            <ActionIcon
                              variant="light"
                              color="green"
                              loading={busy === r.id}
                              onClick={() => setStatus(r, 'active')}
                            >
                              <IconCheck size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {view === 'archived' ? (
                          <Tooltip label={t('knowledge_loop.memory.restore')} withArrow>
                            <ActionIcon variant="subtle" loading={busy === r.id} onClick={() => setStatus(r, 'active')}>
                              <IconArrowBackUp size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip label={t('knowledge_loop.review.edit')} withArrow>
                              <ActionIcon variant="subtle" color="gray" onClick={() => openEditor(r)}>
                                <IconPencil size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('knowledge_loop.memory.archive')} withArrow>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                loading={busy === r.id}
                                onClick={() => setStatus(r, 'archived')}
                              >
                                <IconArchive size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                      </Group>
                    </Group>
                  );
                })}
              </Stack>
            </div>
          ))
      )}

      <Modal
        opened={!!editing}
        onClose={() => setEditing(null)}
        centered
        title={t(editing === 'new' ? 'zendesk_ops.rules.add' : 'zendesk_ops.rules.edit_title')}
      >
        <Stack gap="sm">
          <Select
            label={t('knowledge_loop.memory.field_kind')}
            allowDeselect={false}
            data={REPLY_RULE_KINDS.map((k) => ({ value: k, label: t(`zendesk_ops.rules.kind.${k}`) }))}
            value={formKind}
            onChange={(v) => v && setFormKind(v as ReplyRuleKind)}
          />
          <Textarea
            label={t('knowledge_loop.memory.field_text')}
            description={t(`zendesk_ops.rules.kind_desc.${formKind}`)}
            placeholder={t('zendesk_ops.rules.text_placeholder')}
            autosize
            minRows={3}
            maxLength={300}
            value={formText}
            onChange={(e) => setFormText(e.currentTarget.value)}
          />
          <Text size="xs" c="gray.6">
            {t('zendesk_ops.rules.no_facts_note')}
          </Text>
          <Group justify="flex-end" gap={8}>
            <Button size="xs" variant="default" onClick={() => setEditing(null)}>
              {t('alert_cancel')}
            </Button>
            <Button size="xs" loading={busy === 'form'} disabled={formText.trim().length < 4} onClick={submit}>
              {t('knowledge_loop.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
